import React, { createContext, useContext, useState } from 'react';

interface QRCodeData {
  authSecret?: string;
  keyUri?: string;
  stateToken: string;
  mfActive?: string;
  factorType?: string;
  id?: string;
  isEnabled?: string;
}

interface MfaModalContextProps {
  isMfaLoading: boolean;
  setIsMfaLoading: React.Dispatch<React.SetStateAction<boolean>>;

  tfaCode: string;
  setTfaCode: React.Dispatch<React.SetStateAction<string>>;

  activeModal: number | null;
  setActiveModal: React.Dispatch<React.SetStateAction<number | null>>;
  token: string;
  setToken: React.Dispatch<React.SetStateAction<string>>;
  stateToken: string;
  setStateToken: React.Dispatch<React.SetStateAction<string>>;
  accessToken: string;
  setAccessToken: React.Dispatch<React.SetStateAction<string>>;
  qrCodeData: QRCodeData | null;
  setQrCodeData: React.Dispatch<React.SetStateAction<QRCodeData | null>>;
  verifyCodeArgs: any;
  setVerifyCodeArgs: React.Dispatch<React.SetStateAction<any>>;

  invalidStateToken: boolean;
  setInvalidStateToken: React.Dispatch<React.SetStateAction<boolean>>;

  invalidAuthToken: boolean;
  setInvalidAuthToken: React.Dispatch<React.SetStateAction<boolean>>;

  newQrCodeData: QRCodeData | null;
  setNewQrCodeData: React.Dispatch<React.SetStateAction<QRCodeData | null>>;

  invalidAuthCode: boolean;
  setInvalidAuthCode: React.Dispatch<React.SetStateAction<boolean>>;

  needHelpModal: boolean;
  setNeedHelpModal: React.Dispatch<React.SetStateAction<boolean>>;
  logoutModal: boolean;
  setLogoutModal: React.Dispatch<React.SetStateAction<boolean>>;

  promptAlertModal: any | null;
  setPromptAlertModal: React.Dispatch<React.SetStateAction<boolean>>;
  supportMessageSent: any | null;
  setSupportMessageSent: React.Dispatch<React.SetStateAction<boolean>>;

  allowAccess: { login: boolean; layout: boolean };
  setAllowAccess: React.Dispatch<
    React.SetStateAction<{ login: boolean; layout: boolean }>
  >;

  maxApiLimit: boolean;
  setMaxApiLimit: React.Dispatch<React.SetStateAction<boolean>>;

  pullingInterval: boolean;
  setPullingInterval: React.Dispatch<React.SetStateAction<boolean>>;

  resetModalContext: () => void;
}

const MfaModalContext = createContext<MfaModalContextProps | undefined>(
  undefined,
);

export const useMfaModalContext = (): MfaModalContextProps => {
  const context = useContext(MfaModalContext);
  if (!context) {
    throw new Error('useMfaModalContext must be used within a ModalProvider');
  }
  return context;
};

interface MfaModalProviderProps {
  children: React.ReactNode;
}

export const MfaModalProvider: React.FC<MfaModalProviderProps> = ({
  children,
}) => {
  const [isMfaLoading, setIsMfaLoading] = useState<boolean>(false);
  const [tfaCode, setTfaCode] = useState('');
  const [activeModal, setActiveModal] = useState<number | null>(0);
  const [token, setToken] = useState<string>('');
  const [stateToken, setStateToken] = useState<string>('');
  const [accessToken, setAccessToken] = useState<string>('');
  const [qrCodeData, setQrCodeData] = useState<QRCodeData | null>(null);

  const [needHelpModal, setNeedHelpModal] = useState<boolean>(false);
  const [logoutModal, setLogoutModal] = useState<boolean>(false);

  const [promptAlertModal, setPromptAlertModal] = useState<boolean>(false);
  const [supportMessageSent, setSupportMessageSent] = useState<boolean>(false);
  const [verifyCodeArgs, setVerifyCodeArgs] = useState<any>(null);

  const [maxApiLimit, setMaxApiLimit] = useState<boolean>(false);
  const [pullingInterval, setPullingInterval] = useState<boolean>(false);

  const [newQrCodeData, setNewQrCodeData] = useState<QRCodeData | null>(null);

  const [invalidStateToken, setInvalidStateToken] = useState<boolean>(false);

  const [invalidAuthCode, setInvalidAuthCode] = useState<boolean>(false);

  const [invalidAuthToken, setInvalidAuthToken] = useState<boolean>(false);

  const [allowAccess, setAllowAccess] = useState<{
    login: boolean;
    layout: boolean;
  }>({
    layout: false,
    login: false,
  });

  const resetModalContext = () => {
    setIsMfaLoading(false);
    setActiveModal(0);
    setToken('');
    setStateToken('');
    setAccessToken('');
    setQrCodeData(null);

    setInvalidStateToken(false);

    setAllowAccess({
      login: false,
      layout: false,
    });

    setNeedHelpModal(false);
    setLogoutModal(false);

    setPromptAlertModal(false);
    setSupportMessageSent(false);
    setVerifyCodeArgs(null);

    setPullingInterval(false);
    setNewQrCodeData(null);
    setInvalidAuthCode(false);
    setInvalidAuthToken(false);
    setTfaCode('');
  };

  const value: MfaModalContextProps = {
    isMfaLoading,
    setIsMfaLoading,
    tfaCode,
    setTfaCode,
    activeModal,
    setActiveModal,
    token,
    setToken,
    stateToken,
    setStateToken,
    accessToken,
    setAccessToken,
    qrCodeData,
    setQrCodeData,
    verifyCodeArgs,
    setVerifyCodeArgs,
    needHelpModal,
    setNeedHelpModal,
    logoutModal,
    setLogoutModal,

    promptAlertModal,
    setPromptAlertModal,
    supportMessageSent,
    setSupportMessageSent,

    maxApiLimit,
    setMaxApiLimit,
    resetModalContext,
    pullingInterval,
    setPullingInterval,
    invalidStateToken,
    setInvalidStateToken,
    invalidAuthCode,
    setInvalidAuthCode,
    newQrCodeData,
    setNewQrCodeData,

    invalidAuthToken,
    setInvalidAuthToken,

    allowAccess,
    setAllowAccess,
  };

  return (
    <MfaModalContext.Provider value={value}>
      {children}
    </MfaModalContext.Provider>
  );
};
